// Client-only display preferences (accessibility). Currently just the
// checklist/list/template text-size scale; kept separate from account
// profile data (api/src/lib/profile.ts) since it's a per-device UI
// preference, not something that needs to sync across devices.

export type FontScale = "normal" | "large" | "x-large" | "xx-large" | "xxx-large";

const FONT_SCALE_KEY = "checklist:font-scale:v1";

const SCALES: readonly FontScale[] = ["normal", "large", "x-large", "xx-large", "xxx-large"];

export function isFontScale(value: unknown): value is FontScale {
  return typeof value === "string" && (SCALES as readonly string[]).includes(value);
}

export function getFontScale(): FontScale {
  try {
    const raw = localStorage.getItem(FONT_SCALE_KEY);
    if (isFontScale(raw)) return raw;
  } catch {
    // private mode / quota - fall back to default
  }
  return "normal";
}

export function setFontScale(scale: FontScale): void {
  try {
    localStorage.setItem(FONT_SCALE_KEY, scale);
  } catch {
    // private mode / quota - preference just won't persist this session
  }
}

/** Cycles normal -> large -> x-large -> xx-large -> xxx-large -> normal, used by the header toggle button. */
export function nextFontScale(current: FontScale): FontScale {
  const idx = SCALES.indexOf(current);
  return SCALES[(idx + 1) % SCALES.length]!;
}

/**
 * Reflects the scale onto <html data-font-scale="..."> so the CSS overrides
 * in index.css apply. "normal" removes the attribute entirely (it's the
 * Tailwind default, no override needed). Called once on boot (main.tsx, to
 * avoid an unscaled flash) and again whenever the user toggles.
 */
export function applyFontScale(scale: FontScale): void {
  if (typeof document === "undefined") return;
  if (scale === "normal") {
    delete document.documentElement.dataset.fontScale;
  } else {
    document.documentElement.dataset.fontScale = scale;
  }
}

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  normal: "Normal text size",
  large: "Large text size",
  "x-large": "Extra large text size",
  "xx-large": "Extra extra large text size",
  "xxx-large": "Maximum text size",
};
